import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { getStats } from '../services/api';

// OpsBanner — site-wide yellow strip for unstability warnings, planned
// downtime, "we're rolling something out, hang tight" messages. Sourced
// from the same GET /stats endpoint the marketing widget uses, so ops has
// exactly one lever to pull for user-facing status. Empty banner → nothing
// rendered. Polls every 60s (matches the endpoint's Cache-Control), so
// clearing the banner in DDB propagates without a refresh.
//
// Failure is silent: a decorative status bar must never block the app on
// its own request. Mounts at the top of the App so it sits above the nav
// on every route.
const OpsBanner: React.FC = () => {
  const [banner, setBanner] = React.useState<string>('');

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const s = await getStats();
        if (!cancelled) setBanner((s.banner ?? '').trim());
      } catch {
        // Non-fatal — leave whatever we had (or nothing) in place.
      }
    };
    load();
    const id = window.setInterval(load, 60_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  if (!banner) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-yellow-400 text-yellow-950 border-b border-yellow-500/40"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-2 flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="leading-snug">{banner}</span>
      </div>
    </div>
  );
};

export default OpsBanner;
